<script setup lang="ts">
import type { SwiperContainer } from 'swiper/element'
import sortBy from 'lodash/sortBy'
import { useConfigStore } from '~/store/useConfigStore'
import type { Image, ScheduleEvent, NextEventBanner } from '~/types'

defineOptions({
  name: 'ScheduleEventsNextGameBanner',
})

interface Props {
  scheduleEvent: ScheduleEvent.Model
  nextEventBannersTeam: NextEventBanner.Model[]
}

const store = useConfigStore()
const { nextEventBanners: nextEventBannersSettings } = storeToRefs(store)

const props = defineProps<Props>()

const nextEventBanners = computed<NextEventBanner.Model[]>(() => props.scheduleEvent?.nextEventBanners || [])
const banners = computed<Image[]>(() => {
  const mapperFunction = (banners: NextEventBanner.Model[]): Image[] => {
    return sortBy(banners, 'order').map(item => item.image)
  }
  if (nextEventBanners.value.length > 0) {
    return mapperFunction(nextEventBanners.value)
  }
  if (props.nextEventBannersTeam.length > 0) {
    return mapperFunction(props.nextEventBannersTeam)
  }

  if (nextEventBannersSettings.value.length > 0) {
    return mapperFunction(nextEventBannersSettings.value)
  }

  return []
})

const hasBanners = computed<boolean>(() => banners.value.length > 0)
const showListLink = computed<boolean>(() => banners.value.length > 1)
const swiperContainer = ref<SwiperContainer | null>(null)

useSwiper(swiperContainer, {
  spaceBetween: 12,
  slidesPerView: 1.2,
  navigation: {
    enabled: true,
    nextEl: '.next-game-banners__list-button--next',
    prevEl: '.next-game-banners__list-button--prev',
  },
  breakpoints: {
    768: {
      slidesPerView: 1,
      spaceBetween: 0,
    },
  },
  injectStyles: [
    `:host .swiper {
      overflow: visible;
    }`,
  ],
})
</script>

<template>
  <div
    v-if="hasBanners"
    class="next-game-banners"
  >
    <swiper-container
      ref="swiperContainer"
      :init="false"
    >
      <swiper-slide
        v-for="item in banners"
        :key="`next-game-banners-item-${item.id}`"
        tag="div"
      >
        <UIImage
          :image="item"
          class="next-game-banners__image"
        />
      </swiper-slide>
    </swiper-container>
    <div
      v-if="showListLink"
      class="next-game-banners__list-buttons"
    >
      <UIButton
        prepended-icon="zondicons:arrow-thin-left"
        is-label-hidden
        variant="clear"
        class="next-game-banners__list-button next-game-banners__list-button--prev"
      >
        Previous Slide
      </UIButton>
      <UIButton
        prepended-icon="zondicons:arrow-thin-right"
        is-label-hidden
        variant="clear"
        class="next-game-banners__list-button next-game-banners__list-button--next"
      >
        Next Slide
      </UIButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.next-game-banners {
  @apply w-full relative;
  @apply md:w-1/2;

  swiper-slide {
    @apply w-full !important;
  }

  swiper-container {
    @apply h-full pb-0;
  }

  &__image {
    @apply w-full h-full object-cover;
  }

  &__list-buttons {
    @apply flex items-center gap-[20px];
  }

  &__list-button {
    @apply select-none absolute top-[50%] -mt-3 z-[1] bg-[var(--team-color)] rounded-[2px];
    @apply hover:opacity-50;

    :deep(.button__icon) {
      @apply w-[25px] h-[25px] text-primary;
      @apply hover:text-primary/60;
    }

    &.swiper-button-disabled {
      @apply opacity-50 pointer-events-none;
    }

    &--prev {
      @apply left-0;
    }

    &--next {
      @apply right-0;
    }
  }
}
</style>
